import TableOfContents, {
    TableOfContentsOptions,
    getHeadlineLevel,
    getLinearIndexes,
    TableOfContentData,
    TableOfContentDataItem,
} from '@tiptap/extension-table-of-contents';
import { Editor } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { v4 as uuidv4 } from 'uuid';

// Extend the options interface to include allowEmptyNodes
interface ExtendedTableOfContentsOptions extends TableOfContentsOptions {
    allowEmptyNodes?: boolean;
}

interface TableOfContentsStorage {
    content: TableOfContentData;
    anchors: HTMLElement[];
    scrollPosition: number;
    scrollHandler: () => void;
}

// Extend the original TableOfContentDataItem to include our new properties
interface ExtendedTableOfContentsItem extends TableOfContentDataItem {
    isEmpty: boolean;
}

interface ItemWithPos {
    node: Node;
    pos: number;
}

interface UpdateActiveStatesParams {
    editor: Editor;
    anchorTypes?: string[];
    storage: TableOfContentsStorage;
    onUpdate?: (content: TableOfContentData, isEmpty: boolean) => void;
}

// Helper function to update storage content with proper handling of empty nodes
const updateStorageContent = (params: {
    editor: any;
    storage: any;
    onUpdate?: (content: TableOfContentData, isEmpty: boolean) => void;
    getIndexFn: Function;
    getLevelFn: Function;
    anchorTypes?: string[];
    allowEmptyNodes?: boolean;
}) => {
    const {
        editor,
        storage,
        onUpdate,
        getIndexFn,
        getLevelFn,
        anchorTypes,
        allowEmptyNodes = false
    } = params;

    if (editor.isDestroyed) return;

    const items: ItemWithPos[] = [];
    let content: TableOfContentData = [];
    const domElements: HTMLElement[] = [];

    // Find all nodes that match our anchor types
    editor.state.doc.descendants((node: Node, pos: number) => {
        if (anchorTypes?.includes(node.type.name)) {
            items.push({ node, pos });
        }
    });

    // Process each item
    items.forEach((item, index) => {
        // Skip empty nodes if allowEmptyNodes is false
        if (!allowEmptyNodes && item.node.textContent.length === 0) return;

        const domNode = editor.view.domAtPos(item.pos + 1).node as HTMLHeadingElement;
        const isScrolledOver = storage.scrollPosition >= domNode.offsetTop;
        domElements.push(domNode);

        const originalLevel = item.node.attrs.level;
        const previousItem = items[index - 1];
        const level = getLevelFn(item, content);
        const itemIndex = getIndexFn(item, content, level);

        const newItem: ExtendedTableOfContentsItem = {
            itemIndex,
            id: item.node.attrs['data-toc-id'],
            originalLevel,
            level,
            textContent: item.node.textContent,
            pos: item.pos,
            editor,
            isActive: false,
            isScrolledOver,
            node: item.node,
            dom: domNode,
            isEmpty: item.node.textContent.length === 0
        };

        content = previousItem ? [...content, newItem] : [newItem];
    });

    // Handle active state and scrolled items
    content = updateActiveStates(content, {
        editor,
        anchorTypes,
        storage,
        onUpdate
    });

    if (onUpdate) {
        const isEmpty = content.length === 0;
        onUpdate(content, isEmpty);
    }

    storage.anchors = domElements;
    storage.content = content;

    // Dispatch the updated content to the editor
    editor.state.tr.setMeta('toc', content);
    editor.view.dispatch(editor.state.tr);
};

// Helper function to update active states in content
const updateActiveStates = (content: TableOfContentData, params: UpdateActiveStatesParams): TableOfContentData => {
    const { editor, anchorTypes, storage, onUpdate } = params;
    const activeIds: string[] = [];
    let activeId: string | null = null;

    if (editor.isDestroyed) return content;

    // Find all nodes that match our anchor types
    if (editor.state.doc) {
        editor.state.doc.descendants((node: Node, pos: number) => {
            if (anchorTypes?.includes(node.type.name)) {
                const domNode = editor.view.domAtPos(pos + 1).node;
                if (storage.scrollPosition >= (domNode as HTMLElement).offsetTop) {
                    activeId = node.attrs['data-toc-id'];
                    activeIds.push(node.attrs['data-toc-id']);
                }
            }
        });
    }

    // Update active states
    const updatedContent = content.map(item => ({
        ...item,
        isActive: item.id === activeId,
        isScrolledOver: activeIds.includes(item.id)
    }));

    // Call onUpdate if provided
    if (onUpdate) {
        const isEmpty = content.length === 0;
        onUpdate(updatedContent, isEmpty);
    }

    return updatedContent;
};

export const TableOfContentsExtended = TableOfContents.extend<ExtendedTableOfContentsOptions>({
    // Add additional options
    addOptions() {
        const parentOptions = this.parent?.() || {};

        return {
            ...parentOptions,
            allowEmptyNodes: false,
        };
    },

    // Override plugin creation
    addProseMirrorPlugins() {
        const anchorTypes = this.options.anchorTypes || ['heading'];
        const allowEmptyNodes = this.options.allowEmptyNodes || false;

        return [
            new Plugin({
                key: new PluginKey('tableOfContent'),
                appendTransaction: (transactions, oldState, newState) => {
                    const tr = newState.tr;
                    let changed = false;

                    if (transactions.some(t => t.docChanged)) {
                        const existingIds: string[] = [];

                        newState.doc.descendants((node, pos) => {
                            const tocId = node.attrs['data-toc-id'] as string | null;

                            // Check if we should process this node
                            if (
                                anchorTypes.includes(node.type.name) &&
                                (allowEmptyNodes || node.textContent.length !== 0)
                            ) {
                                if (tocId == null || existingIds.includes(tocId)) {
                                    let newId: string = '';
                                    // Get title from content or use node type as fallback for empty nodes
                                    const title = node.textContent.length > 0
                                        ? node.textContent
                                        : node.type.name;

                                    // Use custom ID generator if provided, otherwise fallback to UUID
                                    newId = this.options.getId ? this.options.getId(title) : uuidv4();

                                    tr.setNodeMarkup(pos, undefined, {
                                        ...node.attrs,
                                        'data-toc-id': newId,
                                    });

                                    changed = true;
                                }

                                if (tocId) {
                                    existingIds.push(tocId);
                                }
                            }
                        });
                    }

                    return changed ? tr : null;
                },
            })
        ];
    },

    // Override commands
    addCommands() {
        return {
            updateTableOfContents: () => ({ dispatch }) => {
                if (dispatch) {
                    updateStorageContent({
                        editor: this.editor,
                        storage: this.storage,
                        onUpdate: this.options.onUpdate?.bind(this),
                        getIndexFn: this.options.getIndex || getLinearIndexes,
                        getLevelFn: this.options.getLevel || getHeadlineLevel,
                        anchorTypes: this.options.anchorTypes,
                        allowEmptyNodes: this.options.allowEmptyNodes
                    });
                }
                return true;
            }
        };
    },

    // Override transaction handler
    onTransaction({ transaction }) {
        if (transaction.docChanged && !transaction.getMeta('toc')) {
            updateStorageContent({
                editor: this.editor,
                storage: this.storage,
                onUpdate: this.options.onUpdate?.bind(this),
                getIndexFn: this.options.getIndex || getLinearIndexes,
                getLevelFn: this.options.getLevel || getHeadlineLevel,
                anchorTypes: this.options.anchorTypes,
                allowEmptyNodes: this.options.allowEmptyNodes
            });
        }
    },

    // Override creation handler
    onCreate() {
        // Set up the scroll handler
        if (this.options.scrollParent) {
            this.storage.scrollHandler = () => {
                if (!this.options.scrollParent) return;

                const scrollParent = typeof this.options.scrollParent === 'function'
                    ? this.options.scrollParent()
                    : this.options.scrollParent;

                const scrollTop = scrollParent instanceof HTMLElement
                    ? scrollParent.scrollTop
                    : scrollParent.scrollY;

                this.storage.scrollPosition = scrollTop || 0;

                // Update active states
                const updatedContent = updateActiveStates(this.storage.content, {
                    editor: this.editor,
                    anchorTypes: this.options.anchorTypes,
                    storage: this.storage,
                    onUpdate: this.options.onUpdate?.bind(this)
                });

                this.storage.content = updatedContent;
            };

            const scrollParent = typeof this.options.scrollParent === 'function'
                ? this.options.scrollParent()
                : this.options.scrollParent;

            scrollParent?.addEventListener('scroll', this.storage.scrollHandler);
        }

        // Initialize content with allowEmptyNodes support
        updateStorageContent({
            editor: this.editor,
            storage: this.storage,
            onUpdate: this.options.onUpdate?.bind(this),
            getIndexFn: this.options.getIndex || getLinearIndexes,
            getLevelFn: this.options.getLevel || getHeadlineLevel,
            anchorTypes: this.options.anchorTypes,
            allowEmptyNodes: this.options.allowEmptyNodes
        });
    },

    // Preserve the onDestroy method from parent
    onDestroy() {
        if (!this.options.scrollParent) return;

        const scrollParent = typeof this.options.scrollParent === 'function'
            ? this.options.scrollParent()
            : this.options.scrollParent;

        scrollParent?.removeEventListener('scroll', this.storage.scrollHandler);
    }
});

export default TableOfContentsExtended;